import { useCallback, useMemo, useRef } from 'react';

// The seek-and-scrub machinery shared by the two charts in the editor's Motion
// section: MotionTimeline (canvas, trim mode) and MotionPaceChart (SVG, pace
// mode). Both drive the SAME <video> — the editor's player shows the SOURCE
// footage, so both charts' x axis is source seconds and a click on either is a
// plain `video.currentTime` write with no curve to invert.
//
// It lives in a hook rather than in either chart because the hard parts are not
// about drawing:
//
//   SEEK-WHEN-READY. A `currentTime` write to a video that is still `seeking`
//   is dropped by the browser, so a drag would land on whichever sample
//   happened to arrive while it was idle. One pending seek is queued instead,
//   and flushed by the caller's `seeked` listener (`onSeeked`) — that is what
//   makes a drag land on the frame the pointer was released over.
//
//   FAST WHILE DOWN, PRECISE ON RELEASE. Keyframe-aligned `fastSeek` decodes
//   near-instantly, so mid-drag samples trade frame accuracy for a picture that
//   keeps up; the release does one exact `currentTime` seek.
//
//   A DISPLAY TIME THAT DOES NOT SNAP BACK. `video.currentTime` still reports
//   the OLD position until a seek completes, so a playhead drawn from it alone
//   jumps backwards under the pointer. `getDisplayTime()` prefers the scrub
//   target, then the queued seek, then the video.
//
// NOTHING HERE IS REACT STATE. Every value is a ref and the returned API is
// referentially stable, so a 60Hz redraw loop calling `getDisplayTime()` never
// triggers a render and the object is safe to put in an effect's dep list.
//
// @param videoRef  a ref whose `.current` is the <video> element (may be null).
// @param domain    the chart's x-axis length in SOURCE seconds; seeks clamp to
//                  it, and to `video.duration` when that is shorter.
// @param onUpdate  called whenever a seek is requested, so the caller can
//                  repaint its playhead before the video has moved.
const useVideoScrub = (videoRef, { domain = 0, onUpdate } = {}) => {
  // The three inputs are re-read through refs so every callback below can be
  // created ONCE and still see the current render's values. That is what keeps
  // the returned API stable.
  const videoRefRef = useRef(videoRef);
  videoRefRef.current = videoRef;
  const domainRef = useRef(domain);
  domainRef.current = domain;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const scrubbingRef = useRef(false);
  const scrubTargetRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const wasPlayingRef = useRef(false);

  const getVideo = useCallback(() => {
    const ref = videoRefRef.current;
    return (ref && ref.current) || null;
  }, []);

  const applySeek = useCallback((video, t) => {
    try {
      if (scrubbingRef.current && typeof video.fastSeek === 'function') {
        video.fastSeek(t);
      } else {
        video.currentTime = t;
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const clampTime = useCallback((t) => {
    const video = getVideo();
    let cap = domainRef.current;
    if (video && Number.isFinite(video.duration)) {
      cap = Math.min(cap, video.duration);
    }
    if (!(cap > 0)) return 0;
    if (t < 0) return 0;
    if (t > cap) return cap;
    return t;
  }, [getVideo]);

  const seekTo = useCallback((t) => {
    const clamped = clampTime(t);
    scrubTargetRef.current = clamped;
    // Repaint FIRST: the playhead has to move with the pointer even when the
    // video is mid-seek and will not report the new position for another frame.
    if (onUpdateRef.current) onUpdateRef.current();
    const video = getVideo();
    if (!video) return clamped;
    if (video.seeking) {
      pendingSeekRef.current = clamped;
    } else {
      applySeek(video, clamped);
    }
    return clamped;
  }, [applySeek, clampTime, getVideo]);

  // Pointer down on the chart: take over playback for the duration of the drag.
  // A playing video is paused and remembered, so the release can resume it.
  const beginScrub = useCallback(() => {
    scrubbingRef.current = true;
    const video = getVideo();
    if (video) {
      wasPlayingRef.current = !video.paused && !video.ended;
      if (wasPlayingRef.current) {
        try {
          video.pause();
        } catch (e) {
          // ignore
        }
      }
    } else {
      wasPlayingRef.current = false;
    }
  }, [getVideo]);

  // Pointer up/cancel. `finalTime` is seeked to AFTER the scrub flag clears, so
  // the last seek of a gesture is the precise one. Returns false when no scrub
  // was in progress, so callers can skip their own end-of-gesture work.
  const endScrub = useCallback((finalTime) => {
    if (!scrubbingRef.current) return false;
    scrubbingRef.current = false;
    if (finalTime != null) seekTo(finalTime);
    scrubTargetRef.current = null;
    const video = getVideo();
    if (wasPlayingRef.current && video) {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
    wasPlayingRef.current = false;
    return true;
  }, [getVideo, seekTo]);

  // Call from the video's `seeked` listener. Flushes the queued seek, or — when
  // the chain has settled outside a drag — drops the target so a later keyboard
  // step cannot base itself on a stale value after an external seek (native
  // controls, another surface).
  const onSeeked = useCallback(() => {
    if (pendingSeekRef.current != null) {
      const t = pendingSeekRef.current;
      pendingSeekRef.current = null;
      const video = getVideo();
      if (video) applySeek(video, t);
    } else if (!scrubbingRef.current) {
      scrubTargetRef.current = null;
    }
  }, [applySeek, getVideo]);

  const getDisplayTime = useCallback(() => {
    const video = getVideo();
    const videoTime = video && Number.isFinite(video.currentTime) ? video.currentTime : 0;
    if (scrubbingRef.current && scrubTargetRef.current != null) return scrubTargetRef.current;
    if (pendingSeekRef.current != null) return pendingSeekRef.current;
    return videoTime;
  }, [getVideo]);

  // What a keyboard step counts FROM. While a seek is in flight `currentTime`
  // still reports the old position, so held arrow keys would recompute the same
  // destination over and over instead of accumulating.
  const getStepBaseTime = useCallback(() => {
    const video = getVideo();
    if (video && video.seeking && scrubTargetRef.current != null) return scrubTargetRef.current;
    return video && Number.isFinite(video.currentTime) ? video.currentTime : 0;
  }, [getVideo]);

  const isScrubbing = useCallback(() => scrubbingRef.current, []);

  return useMemo(() => ({
    seekTo,
    beginScrub,
    endScrub,
    onSeeked,
    getDisplayTime,
    getStepBaseTime,
    isScrubbing,
    clampTime,
  }), [
    seekTo, beginScrub, endScrub, onSeeked,
    getDisplayTime, getStepBaseTime, isScrubbing, clampTime,
  ]);
};

export default useVideoScrub;
